import type { CatalogEvent, ProjectState } from "@livcg/schema";
import { formatEventMinute, isCardEventType } from "@livcg/schema";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button, Card, ConfirmDialog, cn } from "../ui/index.js";
import { sendRaw } from "../shared/sendRaw.js";
import { EditEventModal, type EditPatch } from "./EditEventModal.js";
import { eventLabel, eventTone } from "./eventBadges.js";
import { useEventDelete } from "./useEventDelete.js";
import { useMatchDetail } from "./useMatchDetail.js";

type Props = { slug: string; state: ProjectState };

export function TabLog({ slug, state }: Props) {
  const md = useMatchDetail(state);
  const sorted = md.sortedEvents;
  const [editing, setEditing] = useState<CatalogEvent | null>(null);

  const { requestDelete, confirmProps } = useEventDelete(slug, md);
  const saveEdit = (id: string | undefined, patch: EditPatch) => {
    if (id) void sendRaw("event.update", { slug, eventId: id, ...patch }).then(() => md.refetch());
    setEditing(null);
  };

  return (
    <Card
      title="Event log"
      subtitle={`${sorted.length} ${sorted.length === 1 ? "voqea" : "voqea"} yozilgan`}
      action={
        <Button size="sm" variant="ghost" onClick={() => md.refetch()}>
          Yangilash
        </Button>
      }
    >
      <ul className="flex flex-col gap-1">
        {sorted
          .map((e) => (
            <li
              key={e.id}
              className="grid grid-cols-[60px_auto_1fr_auto] items-center gap-3 rounded-md border border-divider bg-surface-muted/40 px-3 py-2"
            >
              <span className="font-display text-[13px] font-semibold tabular-nums text-ink">
                {formatEventMinute(e.minute, e.second, e.data?.added)}ʼ
              </span>
              <span
                className={cn(
                  "rounded-[5px] border px-2 py-0.5 text-[11px] font-medium",
                  eventTone(e.type),
                )}
              >
                {eventLabel(e.type)}
              </span>
              <span className="min-w-0 truncate text-[13px] text-ink-soft">
                <span className="font-mono text-[11px] text-ink-muted mr-1.5">
                  {md.teamShort(e.teamId)}
                </span>
                {md.playerName(e.playerId)}
                {e.assistPlayerId && (
                  <span className="text-ink-faint">
                    {e.type === "substitution" ? " → " : " · assist "}
                    {md.playerName(e.assistPlayerId)}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Voqeani tahrirlash"
                  onClick={() => setEditing(e)}
                  className="border border-border bg-surface-elevated text-ink-muted hover:text-accent-strong"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Voqeani o'chirish"
                  onClick={() => requestDelete(e)}
                  className="border border-danger/30 bg-danger/10 text-danger-strong hover:bg-danger/20 hover:text-danger-strong"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        {sorted.length === 0 && (
          <li className="rounded-md border border-dashed border-border bg-surface-muted/30 px-4 py-8 text-center text-[13px] text-ink-muted">
            Hozircha voqea yo'q. Events tabidan yangi voqea qo'shing.
          </li>
        )}
      </ul>

      {editing && (
        <EditEventModal
          initial={{
            minute: editing.minute,
            second: editing.second,
            playerId: editing.playerId,
            assistPlayerId: editing.assistPlayerId ?? null,
            added: editing.data?.added ?? 0,
          }}
          eventType={editing.type}
          players={md.teamPlayers(editing.teamId, isCardEventType(editing.type))}
          teamShort={md.teamShort(editing.teamId)}
          typeLabel={eventLabel(editing.type)}
          onClose={() => setEditing(null)}
          onSave={(patch) => saveEdit(editing.id, patch)}
        />
      )}
      <ConfirmDialog {...confirmProps} />
    </Card>
  );
}
